import { useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControlLabel, FormHelperText, Radio, RadioGroup, Switch, TextField, Typography,
} from '@mui/material';
import api from '../services/api';
import { StatusChip } from '../quotas/QuotaTable';
import { safeDecode } from '../utils/decode';
import ConditionBuilder from './ConditionBuilder';
import { indexQuestions } from './conditions';
import { fieldErrors, wranglerError } from './errors';
import LogicSummary from './LogicSummary';
import { fromLogic, sameLogic, toLogic, validate } from './logicModel';
import {
  BuilderArea, FooterActions, Panel, PanelAlerts, PanelHeader, PanelSection, SummaryBox,
} from './PanelLayout';

// A new rule starts OFF, like a new quota: it changes nothing until it's been checked.
export const NEW_RULE = { name: '', is_on: false, condition: null, config: { scope: 'all' } };

const setOption = (name, count) => ({ type: 'set', key: `set:${name}`, name, count });
const quotaOption = (id, name) => ({ type: 'quota', key: `quota:${id}`, id, name });

/**
 * What a rule can be scoped to: every set (by set_name), then every quota except the overall
 * one. Sets and quotas the saved config names are kept even if no quota matches them now.
 */
export const scopeOptions = (quotas, config = {}) => {
  const regular = quotas.filter((q) => !q.is_overall);
  const sizes = new Map();
  regular.forEach((q) => { if (q.set_name) sizes.set(q.set_name, (sizes.get(q.set_name) || 0) + 1); });
  (config.set_names || []).forEach((name) => { if (!sizes.has(name)) sizes.set(name, 0); });
  const sets = [...sizes].sort(([a], [b]) => a.localeCompare(b)).map(([name, count]) => setOption(name, count));
  const known = new Set(regular.map((q) => q.id));
  const missing = (config.quota_ids || []).filter((id) => !known.has(id)).map((id) => quotaOption(id, `Quota ${id}`));
  return [...sets, ...regular.map((q) => quotaOption(q.id, safeDecode(q.name))), ...missing];
};

/** The rule config Wrangler stores, from the editor's choices. */
export const toConfig = (scope, chosen, includeOverall) => (scope === 'all'
  ? { scope: 'all', include_overall: includeOverall }
  : {
    scope: 'quotas',
    quota_ids: chosen.filter((o) => o.type === 'quota').map((o) => o.id),
    set_names: chosen.filter((o) => o.type === 'set').map((o) => o.name),
    include_overall: includeOverall,
  });

const normalConfig = (config = {}) => (config.scope === 'quotas'
  ? {
    scope: 'quotas',
    quota_ids: [...(config.quota_ids || [])].sort((a, b) => a - b),
    set_names: [...(config.set_names || [])].sort(),
    include_overall: Boolean(config.include_overall),
  }
  : { scope: 'all', include_overall: Boolean(config.include_overall) });

const sameConfig = (a, b) => JSON.stringify(normalConfig(a)) === JSON.stringify(normalConfig(b));

/** Wrangler's 400 for a rule, as {name: '...', condition: '...', config: '...'}. */
const errorsByField = (err) => {
  const fields = err?.response?.data?.fields;
  if (!fields || typeof fields !== 'object') return {};
  return Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)]));
};

/**
 * The slide-out rule editor (design §7). `rule` is the saved rule when editing, or a draft when
 * creating. Nothing is sent until Save.
 */
const RuleEditor = ({ surveyId, mode, rule, quotas, questions, onSaved, onDeleted, onClose, onDuplicate }) => {
  const creating = mode === 'create';
  const index = useMemo(() => indexQuestions(questions), [questions]);
  const savedConfig = rule.config || NEW_RULE.config;
  const options = useMemo(() => scopeOptions(quotas, savedConfig), [quotas, savedConfig]);
  const hasOverall = quotas.some((q) => q.is_overall);

  const [name, setName] = useState(rule.name || '');
  const [isOn, setIsOn] = useState(Boolean(rule.is_on));
  const [tree, setTree] = useState(() => fromLogic(rule.condition));
  const [scope, setScope] = useState(savedConfig.scope === 'quotas' ? 'quotas' : 'all');
  // Chosen sets and quotas by key, so their labels follow the quota list if it reloads.
  const [chosenKeys, setChosenKeys] = useState(() => [
    ...(savedConfig.set_names || []).map((n) => setOption(n).key),
    ...(savedConfig.quota_ids || []).map((id) => quotaOption(id).key),
  ]);
  const chosen = useMemo(() => chosenKeys.map((k) => options.find((o) => o.key === k)).filter(Boolean),
    [chosenKeys, options]);
  const [includeOverall, setIncludeOverall] = useState(Boolean(savedConfig.include_overall));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const problems = validate(tree, index);
  const config = toConfig(scope, chosen, includeOverall);
  const scopeEmpty = scope === 'quotas' && chosen.length === 0;
  const conditionChanged = creating || !sameLogic(tree, rule.condition);
  const changes = creating ? null : {
    ...(name.trim() !== rule.name && { name: name.trim() }),
    ...(isOn !== rule.is_on && { is_on: isOn }),
    ...(conditionChanged && problems.length === 0 && { condition: toLogic(tree) }),
    ...(!sameConfig(config, savedConfig) && { config }),
  };
  const dirty = creating || Object.keys(changes).length > 0 || (conditionChanged && problems.length > 0);
  const canSave = dirty && problems.length === 0 && name.trim() !== '' && !scopeEmpty && !busy;

  const act = async (request, after) => {
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      const { data } = await request();
      after(data);
    } catch (err) {
      setError([wranglerError(err), ...fieldErrors(err)].join('\n'));
      setErrors(errorsByField(err));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (creating) {
      const body = { name: name.trim(), is_on: isOn, condition: toLogic(tree), config };
      act(() => api.post(`/surveys/${surveyId}/wrangler/rules/`, body), (data) => {
        onSaved(data, { created: true });
        onClose();
      });
    } else {
      act(() => api.patch(`/surveys/${surveyId}/wrangler/rules/${rule.id}/`, changes), (data) => {
        onSaved(data);
        onClose();
      });
    }
  };

  const remove = () => act(() => api.delete(`/surveys/${surveyId}/wrangler/rules/${rule.id}/`), () => {
    setConfirmDelete(false);
    onDeleted(rule.id);
    onClose();
  });

  const footer = (
    <>
      {error && <Alert severity="error" sx={{ mb: 1.5, whiteSpace: 'pre-line', maxHeight: 160, overflowY: 'auto' }}>{error}</Alert>}
      <FooterActions
        secondary={!creating && (
          <>
            {onDuplicate && (
              <Button onClick={() => onDuplicate(rule)} disabled={busy} sx={{ textTransform: 'none' }}>Duplicate</Button>
            )}
            <Button color="error" onClick={() => setConfirmDelete(true)} disabled={busy} sx={{ textTransform: 'none' }}>Delete</Button>
          </>
        )}
        primary={(
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!canSave} sx={{ textTransform: 'none' }}>
              {busy ? 'Saving…' : creating ? 'Create' : 'Save'}
            </Button>
          </>
        )}
      />
    </>
  );
  const chips = creating ? <StatusChip label="New" colour="grey" />
    : rule.is_on ? <StatusChip label="ON" colour="success" /> : <StatusChip label="OFF" colour="grey" />;

  return (
    <Panel busy={busy} onClose={onClose} footer={footer}>
      <PanelHeader title={creating ? (name.trim() || 'New rule') : rule.name} chips={chips} onClose={onClose} busy={busy} />
      <PanelAlerts>
        <Typography variant="body2" color="text.secondary">
          An exemption lets the people it describes past full quotas. They still count, even above 100%.
          Rules don&apos;t affect Alchemer&apos;s quotas.
        </Typography>
      </PanelAlerts>

      <PanelSection title="Details">
        <TextField label="Name" size="small" fullWidth required value={name} onChange={(e) => setName(e.target.value)}
          error={Boolean(errors.name)} helperText={errors.name} />
      </PanelSection>

      <PanelSection title="Who it applies to">
        <SummaryBox>
          {problems.length === 0
            ? <LogicSummary logic={toLogic(tree)} questions={questions} subject="Applies to people who" variant="body1" />
            : <Typography variant="body1" color="text.secondary">Finish the conditions below to see who this rule applies to.</Typography>}
        </SummaryBox>
        <BuilderArea>
          <ConditionBuilder tree={tree} onChange={setTree} questions={questions} index={index} problems={problems} />
        </BuilderArea>
        {errors.condition && <Alert severity="error" sx={{ mt: 1.5 }}>{errors.condition}</Alert>}
      </PanelSection>

      <PanelSection title="Which quotas it lets them past">
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Which quotas">
          <FormControlLabel value="all" control={<Radio size="small" />} label="All quotas" />
          <FormControlLabel value="quotas" control={<Radio size="small" />} label="Chosen quotas and sets" />
        </RadioGroup>
        {scope === 'all' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            These people get past every full quota in the survey, including quotas added later.
            {!includeOverall && hasOverall && ' The overall quota still stops them unless the box below is ticked.'}
          </Alert>
        )}
        {scope === 'quotas' && (
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={options}
              groupBy={(o) => (o.type === 'set' ? 'Sets' : 'Quotas')}
              getOptionLabel={(o) => (o.type === 'set' ? `${o.name} (set of ${o.count})` : o.name)}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              value={chosen}
              onChange={(e, value) => setChosenKeys(value.map((o) => o.key))}
              renderTags={(value, getTagProps) => value.map((o, i) => {
                const { key, ...tagProps } = getTagProps({ index: i });
                return <Chip key={key} size="small" label={o.type === 'set' ? `Set: ${o.name}` : o.name} {...tagProps} />;
              })}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Quotas and sets" placeholder="Search"
                  error={scopeEmpty || Boolean(errors.config)} />
              )}
            />
            <FormHelperText error={scopeEmpty}>
              {scopeEmpty
                ? 'Choose at least one quota or set.'
                : 'A set covers every quota in it, including quotas added to it later. Chosen quotas stay as chosen.'}
            </FormHelperText>
          </Box>
        )}
        {errors.config && <Alert severity="error" sx={{ mt: 1 }}>{errors.config}</Alert>}
        <FormControlLabel
          sx={{ mt: 1.5, display: 'flex' }}
          control={<Checkbox size="small" checked={includeOverall} onChange={(e) => setIncludeOverall(e.target.checked)} />}
          label="Also allow past the overall quota"
        />
        <FormHelperText sx={{ mt: -0.5, ml: 4 }}>
          {includeOverall
            ? 'These people get in even when the survey as a whole is full.'
            : "Without this, the survey's overall quota still stops them when it's full."}
          {!hasOverall && ' This survey has no overall quota in Wrangler yet.'}
        </FormHelperText>
      </PanelSection>

      <PanelSection title="Status">
        <FormControlLabel
          control={<Switch checked={isOn} onChange={(e) => setIsOn(e.target.checked)} />}
          label={isOn ? 'ON: lets these people past full quotas' : 'OFF: has no effect'}
        />
      </PanelSection>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {rule.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The people it describes are stopped by full quotas again. This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
};

export default RuleEditor;
